import { useQuery, useQueryClient } from '@tanstack/react-query'

import { IActivityItem } from '@/components/pages/Activity/interfaces'

import { cacheActivitiesReactions } from '@/utils/reactions'

import { QueryKeys } from '@/enums'

import { useConversationId } from './useConversationId'
import { useGetstreamClient } from './useGetstreamClient'

interface IUseActivityDetails {
  activityId: UUID | 'conversation'
  replyObjectId: UUID
  createdAt: string
}

export const useActivityDetails = ({
  activityId,
  replyObjectId,
  createdAt,
}: IUseActivityDetails) => {
  const { client } = useGetstreamClient()
  const queryClient = useQueryClient()

  const { conversationId } = useConversationId({
    activityId,
    replyObjectId,
    createdAt,
  })

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [QueryKeys.ActivityDetails, conversationId],
    queryFn: async () => {
      const response = await client.getActivities({
        ids: [conversationId],
        reactions: {
          counts: true,
          own: true,
          recent: true,
        },
      })
      const [activity] = response.results

      cacheActivitiesReactions([activity as unknown as IActivityItem])

      queryClient.setQueryData(
        [QueryKeys.CommentsData, conversationId],
        (prevData) => {
          const latestReactions = activity.latest_reactions as {
            COMMENT?: []
          }
          return prevData ?? latestReactions.COMMENT
        }
      )

      return activity as unknown as IActivityItem
    },
    enabled: !!client && !!conversationId,
  })

  return {
    activity: data,
    isLoading,
    hasError: !!error,
    refetch,
  }
}
